import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { useRevealStyles } from '@vibesites/theme-prestige/hooks/useRevealStyles';
import {
  AmenityColumn,
  AmenitiesStyles,
  stripHtml,
  type Amenity,
  type GalleryPhoto,
} from '@vibesites/theme-prestige/components/AmenitiesShared';

export function InteriorAmenitiesList() {
  const headingRaw = useContentToken('interioramenitieslist.heading');
  const headingText = stripHtml(headingRaw) || 'Interior Luxuries';

  const items = useCollectionBinding<Amenity>('interioramenitieslist.items', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const photos = useCollectionBinding<GalleryPhoto>('interioramenitieslist.photos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {items.length > 0 ? (
          <>
            <AmenityColumn
              headingNode={
                <RichTextContent
                  path="interioramenitieslist.heading"
                  label="Interior Heading"
                  defaultValue="<p>Interior Luxuries</p>"
                  as="h3"
                  className="amenities-col-heading"
                />
              }
              headingText={headingText}
              items={items}
              photos={photos}
              imagePosition="right"
            />
            <RichTextContent
              path="interioramenitieslist.footnote"
              label="Interior Footnote"
              defaultValue="<p>*in selected homes</p>"
              as="p"
              className="amenities-col-footnote"
            />
          </>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Interior amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <AmenitiesStyles />

      <style>{`
        .amenities-col-footnote {
          margin: 0.75rem 0 0;
          padding: 0 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-style: italic;
          color: var(--vibe-color-text-muted, var(--vibe-color-text));
          text-align: left;
        }
        @media (max-width: 900px) {
          .amenities-col-footnote {
            padding: 0 1rem;
            font-size: 0.75rem;
          }
        }
        @media (max-width: 600px) {
          .amenities-col-footnote {
            padding: 0 0.75rem;
            font-size: 0.7rem;
            text-align: center;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InteriorAmenitiesList',
  displayName: 'Interior Amenities List',
  tokens: {
    'interioramenitieslist.heading': {
      label: 'Interior Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Luxuries</p>',
    },
    'interioramenitieslist.footnote': {
      label: 'Interior Footnote',
      type: 'richtext',
      defaultValue: '<p>*in selected homes</p>',
    },
  },
  collectionBindings: {
    'interioramenitieslist.items': {
      label: 'Interior Features',
      collection: 'amenities',
    },
    'interioramenitieslist.photos': {
      label: 'Interior Photos',
      collection: 'photo_gallery',
    },
  },
};

(InteriorAmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default InteriorAmenitiesList;
